import { ApiError } from '@/lib/api'
import { messageFor } from '@/lib/errors'

/**
 * The words the sign-in screens use for failure.
 *
 * Two rules shape this file.
 *
 * The first is non-disclosure. Taskly must never tell an anonymous visitor
 * whether an address has an account: the sign-in form takes the email and the
 * password before it asks the server anything, and whatever comes back — an
 * unknown address, a wrong password, a locked account — is reported with one
 * sentence. Jira leaks this (it looks up the address on the first step); we
 * deliberately do not.
 *
 * The second is that a throttled visitor is not a broken one. "Too Many
 * Attempts." is the API's literal 429 body, and showing it verbatim reads as an
 * error the person caused and cannot fix. The Retry-After header turns it into
 * an instruction instead.
 */

/** The one answer a failed sign-in ever gives. */
export const INVALID_CREDENTIALS = 'That email or password is incorrect.'

/** Phrases a wait in whole units, because "in 47 seconds" is not readable. */
function waitFor(seconds: number | null): string {
  if (seconds === null || seconds <= 0) return 'a moment'
  // Rounded up to five seconds: the countdown is advice, and "47 seconds" reads
  // as a promise the page cannot keep.
  const rounded = Math.max(5, Math.ceil(seconds / 5) * 5)
  if (rounded < 60) return `${rounded} seconds`
  const minutes = Math.ceil(rounded / 60)
  return minutes === 1 ? 'a minute' : `${minutes} minutes`
}

/** The 429 for a given action, phrased as a wait rather than as an error. */
export function throttleMessage(error: ApiError, action: string): string {
  return `Too many ${action}. Wait ${waitFor(error.retryAfter)} and try again.`
}

/**
 * The form-level sentence for a failed sign-in.
 *
 * A 422 here is always the credential check — the form validates the shape of
 * both fields before it submits — so it collapses into the generic message
 * rather than surfacing the API's field-mapped text, which would put "The
 * provided credentials are incorrect." under an email input that is no longer
 * even on screen.
 */
export function signInErrorMessage(error: unknown): string | null {
  if (!error) return null
  if (error instanceof ApiError) {
    if (error.isRateLimited) return throttleMessage(error, 'sign-in attempts')
    if (error.status === 401 || error.status === 422) return INVALID_CREDENTIALS
  }
  return messageFor(error)
}

/**
 * The form-level sentence for the screens that DO have per-field errors —
 * sign-up, and both halves of a password reset. Their 422s are real validation
 * failures and belong under the field they name, so this returns null for them
 * and only speaks when there is nothing inline to show.
 */
export function authErrorMessage(error: unknown, fields: string[], action: string): string | null {
  if (!error) return null
  if (error instanceof ApiError) {
    if (error.isRateLimited) return throttleMessage(error, action)
    if (fields.some((field) => error.fieldError(field))) return null
  }
  return messageFor(error)
}

/**
 * Client-side email check for the first step.
 *
 * It is deliberately shallow: enough to catch a typo before a round trip, and
 * nowhere near enough to reject an address the server would accept. It runs in
 * the browser only, so it cannot leak whether the address is registered.
 *
 * The shape check is a local part, an `@`, and a domain — nothing more. The
 * server validates with `net/mail`, which accepts dotless domains, so requiring
 * a dot here would lock out an address the API would happily authenticate with
 * no round trip left to appeal to. The server stays the authority.
 */
export function emailProblem(value: string): string | undefined {
  const email = value.trim()
  if (!email) return 'Enter your email address.'
  if (!/^[^\s@]+@[^\s@]+$/.test(email)) {
    return 'Enter a valid email address, like you@example.com.'
  }
  return undefined
}
